import React, { useState, useRef, useEffect } from 'react'
import axios from 'axios' // Importa Axios

import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid2'
import MovieIcon from '@mui/icons-material/Movie'

import { openFeedback } from '../../store/slices/feedback'
import { useGetSignedUrlMutation, useRegistrarArchivoMutation } from '../../store/api/endpoints/workhopsUploadVideoApiSlice'

import Loading from './Loading'
import { useDispatch } from 'react-redux'

export default function VideoUploader({ idObjeto, onVideoSelected, buttonText = 'Seleccionar Video' }) {
    const dispatch = useDispatch()

    const inputFileRef = useRef(null)
    const [selectedFile, setSelectedFile] = useState(null) // Nuevo estado para el archivo
    const [selectedFileName, setSelectedFileName] = useState('')
    const [isUploadingFile, setIsUploadingFile] = useState(false)
    const [progresoUploadingFile, setProgresoUploadingFile] = useState(0)

    const [
        getSignedUrl,
        {
            data: responseGetSignedUrl,
            isLoading: isLoadingGetSignedUrl,
            isSuccess: isSuccessGetSignedUrl,
            isError: isErrorGetSignedUrl,
            error: errorDataGetSignedUrl,
        }
    ] = useGetSignedUrlMutation()

    const [
        confirmarSubida,
        {
            data: responseRegistrarArchivo,
            isLoading: isLoadingRegistrarArchivo,
            isSuccess: isSuccessRegistrarArchivo,
            isError: isErrorRegistrarArchivo,
            error: errorDataRegistrarArchivo,
        }
    ] = useRegistrarArchivoMutation()

    // 🚩 1. Subida del archivo al bucket con Axios (PUT)
    const uploadFileToBucket = async (file) => {
        setIsUploadingFile(true)
        try {
            // El `Content-Type` debe ser el del archivo
            await axios.put(responseGetSignedUrl.signedUrl, file, {
                onUploadProgress: (progressEvent) => {
                    const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
                    setProgresoUploadingFile(percentCompleted)
                },
            })
            setIsUploadingFile(false)
            onVideoSelected(file) // Informa al padre que la subida ha terminado
            confirmarSubida({
                idObjeto,
                body: { key: responseGetSignedUrl.key }
            })
        } catch (error) {
            setIsUploadingFile(false)
            dispatch(openFeedback({
                severity: 'error',
                message: `Error al subir el video: ${error.message}`
            }))
            console.error('Error al subir el archivo:', error)
        }
    }

    // 🚩 2. Maneja el cambio del input de archivo
    const handleFileChange = (event) => {
        if (event.target.files && event.target.files.length > 0) {
            const file = event.target.files[0]
            setSelectedFile(file) // Guarda el archivo seleccionado
            setSelectedFileName(file.name)

            // Inicia la petición de la URL firmada
            getSignedUrl({
                idObjeto,
                body: { contentType: "video/mp4" }
            })
        } else {
            setSelectedFile(null)
            setSelectedFileName('')
            onVideoSelected(null)
        }
    }

    const handleClickSelectFile = () => {
        inputFileRef.current.click()
    }

    // 🚩 3. Maneja el resultado de la URL firmada
    useEffect(() => {
        if (isErrorGetSignedUrl) {
            dispatch(openFeedback({
                severity: 'error',
                message: errorDataGetSignedUrl?.data?.message || 'Ocurrió un error al conectar con el storage de archivos.'
            }))
        }
    }, [isErrorGetSignedUrl, errorDataGetSignedUrl, dispatch])

    // 🚩 4. Si la URL se obtiene con éxito, inicia la subida del archivo
    useEffect(() => {
        if (isSuccessGetSignedUrl && selectedFile) {
            uploadFileToBucket(selectedFile)
        }
    }, [isSuccessGetSignedUrl, selectedFile]) // Dependencias: la URL y el archivo

    if (isLoadingGetSignedUrl || isLoadingRegistrarArchivo) {
        return (<><Loading />Conectando al storage de archivos...</>)
    }

    if (isUploadingFile) {
        return (<><Loading />Subiendo video ({progresoUploadingFile}% completado)...</>)
    }

    return (
        <Grid container spacing={2} alignItems="center">
            <Grid item align="center">
                <Button
                    variant="outlined"
                    startIcon={<MovieIcon />}
                    onClick={handleClickSelectFile}
                    disabled={isUploadingFile || isLoadingGetSignedUrl}
                >
                    {buttonText}
                </Button>
                <input
                    type="file"
                    accept="video/*"
                    ref={inputFileRef}
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                />
            </Grid>
            <Grid item>
                {selectedFileName && (
                    <p style={{ margin: 0, color: 'rgba(0, 0, 0, 0.6)' }}>
                        Archivo seleccionado: <strong>{selectedFileName}</strong>
                    </p>
                )}
            </Grid>
        </Grid>
    )
}